import React, { ReactNode } from 'react'
import { Control, Controller } from 'react-hook-form'
import { RegisterOptions } from 'react-hook-form/dist/types/validator'
import { FieldErrors } from 'react-hook-form/dist/types/errors'
import TextField from '@mui/material/TextField'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import whiteCheck from 'assets/icons/white-check.svg'

interface SelectControllerProps {
  defaultValue?: any
  label: string
  name: string
  children: ReactNode
  control: Control
  rules?: RegisterOptions
  errors: FieldErrors
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: '20px 0 0',
      '& .MuiSelect-icon': {
        right: 10,
      },
      '& .MuiInputBase-root': {
        marginTop: 0,
      },
      '& .MuiInputBase-input': {
        borderRadius: 30,
        backgroundColor: theme.colors.greySemiLight,
        padding: '10px 16px 11px',
        boxSizing: 'border-box',
        height: 40,

        [theme.breakpoints.up(1024)]: {
          padding: '14px 24px 15px',
          height: 48,
        },
      },
      '& .MuiInputLabel-root': {
        transform: 'translate(16px, 34px)',
        maxWidth: 'calc(100% - 16px)',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        color: theme.colors.greySemiDark,

        [theme.breakpoints.up(1024)]: {
          maxWidth: 'calc(100% - 24px)',
          transform: 'translate(24px, 35px)',
        },

        [theme.breakpoints.down(1023)]: {
          fontSize: 12,
        },
      },
      '& .MuiInputLabel-shrink': {
        transform: 'translate(16px, 0)!important',
        fontSize: 12,

        [theme.breakpoints.up(1024)]: {
          transform: 'translate(24px, 0)!important',
        },
      },
      '& .MuiFormHelperText-root': {
        minHeight: 20,
        fontFamily: theme.fonts.inter,
        color: theme.colors.red,
        letterSpacing: 'normal',
        fontSize: 12,
        lineHeight: 1.5,
        maxWidth: '100%',
        paddingLeft: 16,
        marginTop: 0,

        [theme.breakpoints.up(1024)]: {
          paddingLeft: 24,
        },
      },
    },
    menuPaper: {
      boxShadow: `
        0px 40px 80px rgba(173, 173, 173, 0.07),
        0px 20px 33.4221px rgba(173, 173, 173, 0.0503198),
        0px 10px 17.869px rgba(173, 173, 173, 0.0417275),
        0px 4px 10.0172px rgba(173, 173, 173, 0.035),
        0px 2px 5.32008px rgba(173, 173, 173, 0.0282725),
        0px 0px 2.21381px rgba(173, 173, 173, 0.0196802);
      `,
      borderRadius: 8,
      '& .MuiListItem-root': {
        '& .select-checkbox': {
          position: 'relative',
          paddingLeft: 24,

          '& .select-checkbox__icon': {
            position: 'absolute',
            left: 0,
            top: 4,
            display: 'block',
            width: 16,
            height: 16,
            borderRadius: 4,
            border: `1px solid ${theme.colors.black}`,
          },
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: theme.colors.greyLight,
        },
        '&.Mui-selected': {
          '& .select-checkbox__icon': {
            background: `${theme.colors.black} url(${whiteCheck}) no-repeat 40% 60%`,
            backgroundSize: 9,
          },
        },
      },
    },
  })
)

const SelectController = (props: SelectControllerProps) => {
  const { name, label, children, defaultValue, control, rules, errors } = props
  const classes = useStyles()

  return (
    <div>
      <Controller
        render={({ field: { onChange, value, name, ref } }) => (
          <TextField
            fullWidth
            InputLabelProps={{
              variant: 'filled',
            }}
            classes={{
              root: classes.root,
            }}
            SelectProps={{
              MenuProps: {
                variant: 'menu',
                classes: {
                  paper: classes.menuPaper,
                },
                transformOrigin: {
                  vertical: -54,
                  horizontal: 'left',
                },
              },
            }}
            defaultValue={defaultValue}
            value={value || ''}
            onChange={onChange}
            inputRef={ref}
            label={label}
            id={name}
            helperText={<span>{errors?.[name]?.message}</span>}
            select
          >
            {children}
          </TextField>
        )}
        defaultValue={defaultValue}
        name={name}
        control={control}
        rules={rules}
      />
    </div>
  )
}

export default SelectController
